import {useState, useEffect} from "react";
import {useSocket} from "src/hooks/socket/useSocket";
import {
  QueueEnteringResponse,
  WaitingQueueInfo,
  WaitingQueueStatus,
} from "../logic/types/waitingQueue.types";
import {ConnectionState} from "src/hooks/socket/socket.type";
import {
  setQueueStatusQueryData,
  useGetQueueStatusQuery,
} from "../logic/quries/waitingQueue.queries";

const SOCKET_EMIT_EVENTS = {
  QUEUE_COMPLETE: "queue:entering",
};

const SOCKET_ON_EVENTS = {
  QUEUE_INFO: "queue:info",
  QUEUE_ENTERING: "queue:entering",
};

const QUEUE_STATUS_POLLING_INTERVAL_MS = 30_000; // 30초

export const useWaitingQueue = ({
  registeredQueueInfo,
}: {
  registeredQueueInfo?: WaitingQueueInfo;
}) => {
  const {connectionState, connect, disconnect, on, off, emit} = useSocket();
  const [waitingQueueInfo, setWaitingQueueInfo] = useState<
    WaitingQueueInfo | undefined
  >(registeredQueueInfo);
  const [enterCodeAllowed, setEnterCodeAllowed] = useState(false);

  const {data: queueStatusData, isSuccess: isQueueStatusSuccess} =
    useGetQueueStatusQuery({
      initialData: registeredQueueInfo,
      enabled: registeredQueueInfo?.wsEligible === false,
      refetchInterval: (query) =>
        query.state.data?.wsEligible === false
          ? QUEUE_STATUS_POLLING_INTERVAL_MS
          : false,
    });

  useEffect(() => {
    if (isQueueStatusSuccess && queueStatusData) {
      console.log("isQueueStatusSuccess", isQueueStatusSuccess);
      console.log("queueStatusData", queueStatusData);
      setWaitingQueueInfo(queueStatusData);
    }
  }, [isQueueStatusSuccess, queueStatusData]);

  const waitingQueueSocketConnect = async (): Promise<void> => {
    try {
      await connect(`${process.env.REACT_APP_SOCKET_SERVER_URL}/queue`, {
        transports: ["websocket"],
        withCredentials: true,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const subscribeWaitingQueueSocketEvent = (): void => {
    on(SOCKET_ON_EVENTS.QUEUE_INFO, (data: unknown) => {
      console.log(`${SOCKET_ON_EVENTS.QUEUE_INFO} :: received`, data);
      const info = data as WaitingQueueInfo;
      setQueueStatusQueryData(info);

      if (info.status === WaitingQueueStatus.ACTIVE) {
        emit(SOCKET_EMIT_EVENTS.QUEUE_COMPLETE, "");
      }
    });

    on(SOCKET_ON_EVENTS.QUEUE_ENTERING, (data: unknown) => {
      const response = data as QueueEnteringResponse;
      if (response.ok === true) {
        disconnect();
        setEnterCodeAllowed(true);
      }
    });
  };

  const unsubscribeWaitingQueueSocketEvent = (): void => {
    off(SOCKET_ON_EVENTS.QUEUE_INFO);
    off(SOCKET_ON_EVENTS.QUEUE_ENTERING);
  };

  // wsEligible === true일 때 소켓 연결
  useEffect(() => {
    if (waitingQueueInfo?.wsEligible === true) {
      console.log("socket connect");
      waitingQueueSocketConnect();
    }
  }, [waitingQueueInfo?.wsEligible]);

  // 소켓 연결 상태가 CONNECTED일 때 자동으로 이벤트 구독
  useEffect(() => {
    if (connectionState === ConnectionState.CONNECTED) {
      subscribeWaitingQueueSocketEvent();
    }

    return () => {
      unsubscribeWaitingQueueSocketEvent();
    };
  }, [connectionState]);

  // 언마운트 될 때 disconnect 처리
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  return {
    waitingQueueInfo,
    enterCodeAllowed,
  };
};
